import React from 'react';
import { BgImage } from '../components/Img';
import { IMG } from '../images';
import { Link } from 'react-router-dom';
import { Shield, Zap, Target, Users, Award, CheckCircle } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: <Zap size={40} />,
      title: 'Speed & Efficiency',
      description: 'We understand that time matters. We plan carefully and execute efficiently to minimise downtime and disruption.'
    },
    {
      icon: <Target size={40} />,
      title: 'Solutions & Rapid Response',
      description: "We don't just identify problems—we solve them, especially when the situation is urgent."
    },
    {
      icon: <Shield size={40} />,
      title: 'High-Complexity Buildings',
      description: 'We specialise in buildings and structures that are difficult to access, architecturally unique or technically demanding.'
    },
    {
      icon: <Award size={40} />,
      title: 'Certified at Height',
      description: 'All our work is carried out by IRATA Level 3 certified height workers and rope access technicians.'
    },
    {
      icon: <Users size={40} />,
      title: 'Experience You Can Trust',
      description: 'Our team brings more than a decade of real-world experience on ropes, working in demanding environments.'
    }
  ];

  const whyChoose = [
    'Small, agile team with direct communication',
    'Hands-on experience with high-risk and high-complexity jobs',
    'Full-service offering: cleaning, remedial, glazing, height safety, bird control and emergencies',
    'Strong safety culture and compliance',
    'Visual documentation of our work (photos and videos)',
    'Available across NSW and Australia for specialised projects'
  ];

  return (
    <div>
      {/* Hero Section */}
      <section 
        className="hero" 
        style={{
          minHeight: '60vh'
        }}
      >
        <BgImage image={IMG.facade} position="50% 40%" priority />
        <div className="hero-content">
          <h1>About ProClimb Solutions</h1>
          <p>Professional rope access specialists with over 10 years of experience</p>
        </div>
      </section>

      {/* Our Story */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2>Our Story</h2>
            <p>Built on expertise, driven by safety and excellence</p>
          </div>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <p style={{ fontSize: '1.2rem', lineHeight: 1.8, color: 'var(--steel-gray)', marginBottom: '1.5rem' }}>
              ProClimb Solutions was created by rope access professionals with a simple goal: to provide fast, efficient and safe solutions for high-risk and high-complexity work at height.
            </p>
            <p style={{ fontSize: '1.2rem', lineHeight: 1.8, color: 'var(--steel-gray)', marginBottom: '1.5rem' }}>
              We started as a small, highly skilled team with extensive experience working on some of the most challenging buildings and structures in New South Wales. After years of working for other companies, we decided to build a service that puts responsiveness, quality and safety at the centre of everything we do.
            </p>
            <p style={{ fontSize: '1.2rem', lineHeight: 1.8, color: 'var(--steel-gray)' }}>
              Today, ProClimb Solutions combines over 10+ years of rope access and height work experience with multi-skilled technicians capable of handling cleaning, remedial, glazing and maintenance work. We have a strong focus on emergency response and complex access challenges.
            </p>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="section bg-light">
        <div className="container">
          <div className="section-header">
            <h2>Our Values</h2>
            <p>What drives us to deliver excellence in every project</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
            {values.map((value, index) => (
              <div 
                key={index}
                style={{
                  background: 'var(--white)',
                  padding: '2rem',
                  border: '1px solid var(--border-color)',
                  transition: 'all 0.3s'
                }}
                className="trust-item"
              >
                <div style={{ color: 'var(--navy-blue)', marginBottom: '1rem' }}>
                  {value.icon}
                </div>
                <h3 style={{ color: 'var(--navy-blue)', marginBottom: '0.75rem', fontSize: '1.3rem' }}>
                  {value.title}
                </h3>
                <p style={{ color: 'var(--steel-gray)' }}>{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Clients Choose Us */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2>Why Clients Choose Us</h2>
          </div>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1rem' }}>
              {whyChoose.map((reason, index) => (
                <div 
                  key={index}
                  style={{
                    display: 'flex',
                    gap: '1rem',
                    alignItems: 'flex-start',
                    padding: '1rem',
                    background: 'var(--light-gray)',
                    border: '1px solid var(--border-color)'
                  }}
                >
                  <CheckCircle size={24} style={{ color: 'var(--safety-orange)', flexShrink: 0 }} />
                  <span style={{ fontSize: '1.1rem', color: 'var(--industrial-black)' }}>{reason}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="section bg-dark">
        <div className="container">
          <div className="section-header">
            <h2 style={{ color: 'var(--white)' }}>Safety & Certification</h2>
            <p style={{ color: '#e5e7eb' }}>
              All our rope access work is carried out by fully trained and certified technicians, following strict safety procedures and industry best practices.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '2rem', marginTop: '3rem' }}>
            <div style={{ background: 'rgba(255, 255, 255, 0.1)', padding: '2rem', border: '1px solid rgba(255, 255, 255, 0.2)', textAlign: 'center' }}>
              <Award size={48} style={{ color: 'var(--safety-orange)', marginBottom: '1rem' }} />
              <h3 style={{ color: 'var(--white)', marginBottom: '0.5rem', fontSize: '1.3rem' }}>IRATA Level 3 Certified</h3>
              <p style={{ color: '#e5e7eb' }}>Highest level of rope access certification</p>
            </div>
            <div style={{ background: 'rgba(255, 255, 255, 0.1)', padding: '2rem', border: '1px solid rgba(255, 255, 255, 0.2)', textAlign: 'center' }}>
              <Shield size={48} style={{ color: 'var(--safety-orange)', marginBottom: '1rem' }} />
              <h3 style={{ color: 'var(--white)', marginBottom: '0.5rem', fontSize: '1.3rem' }}>Fully Insured</h3>
              <p style={{ color: '#e5e7eb' }}>Comprehensive insurance coverage</p>
            </div>
            <div style={{ background: 'rgba(255, 255, 255, 0.1)', padding: '2rem', border: '1px solid rgba(255, 255, 255, 0.2)', textAlign: 'center' }}>
              <CheckCircle size={48} style={{ color: 'var(--safety-orange)', marginBottom: '1rem' }} />
              <h3 style={{ color: 'var(--white)', marginBottom: '0.5rem', fontSize: '1.3rem' }}>WHS Compliance</h3>
              <p style={{ color: '#e5e7eb' }}>Australian standards and regulations</p>
            </div>
            <div style={{ background: 'rgba(255, 255, 255, 0.1)', padding: '2rem', border: '1px solid rgba(255, 255, 255, 0.2)', textAlign: 'center' }}>
              <Users size={48} style={{ color: 'var(--safety-orange)', marginBottom: '1rem' }} />
              <h3 style={{ color: 'var(--white)', marginBottom: '0.5rem', fontSize: '1.3rem' }}>10+ Years Experience</h3>
              <p style={{ color: '#e5e7eb' }}>Proven track record on complex structures</p>
            </div>
          </div>

          <div style={{ marginTop: '3rem', padding: '2rem', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <h3 style={{ color: 'var(--safety-orange)', marginBottom: '1rem', fontSize: '1.3rem' }}>We ensure:</h3>
            <ul style={{ color: '#e5e7eb', fontSize: '1.1rem', lineHeight: 1.8, listStyle: 'none', padding: 0 }}>
              {[
                'Certified work at height and rope access technicians',
                'Compliance with Australian standards and WHS regulations',
                'Detailed risk assessments and method statements for each job',
                'Proper use and inspection of all equipment and anchor systems',
                'More than a decade of experience working at height on complex structures'
              ].map((item, index) => (
                <li key={index} style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <CheckCircle size={20} style={{ color: 'var(--safety-orange)', flexShrink: 0, marginTop: '3px' }} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 style={{ marginBottom: '1.5rem' }}>Ready to Work With Us?</h2>
          <p style={{ fontSize: '1.2rem', color: 'var(--steel-gray)', marginBottom: '2rem' }}>
            Get in touch for a free consultation and quote
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" className="btn-primary">Contact Us</Link>
            <Link to="/services" className="btn-secondary" style={{ border: '2px solid var(--industrial-black)', color: 'var(--industrial-black)' }}>
              View Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
